import React from 'react';
import { Box, Chip, Icon } from '@saviynt/design-system';

import { SodRiskScore } from '../../../shared/components';
import { useInvertedLocalize } from '../../../shared/hooks';
import { type TCodeData } from '../../../shared/types';
import TCodeListRow from '../TCodeListRow';

import './InsightsList.css';

/**
 * Props interface for the InsightsList component
 */
interface InsightsListProps {
  tCodeData: TCodeData[];
  onCodeClick: (codeId: string) => void;
  className?: string;
  riskScore?: number;
}

/**
 * InsightsList Component
 * Displays the insights list with risk level and T-codes.
 * Shows the initial view before a Entitlement is selected.
 */
function InsightsList({
  tCodeData,
  onCodeClick,
  className,
  riskScore,
}: InsightsListProps): React.ReactElement {
  const l = useInvertedLocalize();

  const normalizedScore = React.useMemo(() => {
    if (typeof riskScore !== 'number') {
      return undefined;
    }

    const n = Number(riskScore);

    if (!Number.isFinite(n)) {
      return undefined;
    }

    return Math.max(0, Math.min(100, n));
  }, [riskScore]);

  const showRisk = typeof normalizedScore === 'number' && normalizedScore > 0;

  const riskKind: 'success' | 'warning' | 'critical' | undefined =
    React.useMemo(() => {
      if (!showRisk || typeof normalizedScore !== 'number') {
        return undefined;
      }

      if (normalizedScore <= 30) {
        return 'success';
      }

      if (normalizedScore <= 59) {
        return 'warning';
      }

      return 'critical';
    }, [normalizedScore, showRisk]);

  const riskLabelKey = React.useMemo(() => {
    if (!showRisk || typeof normalizedScore !== 'number') {
      return '';
    }

    if (normalizedScore <= 30) {
      return l('Low Risk');
    }

    if (normalizedScore <= 59) {
      return l('Medium Risk');
    }

    return l('High Risk');
  }, [normalizedScore, showRisk, l]);

  const riskIconKind = React.useMemo<'Happy' | 'Neutral' | 'Sad'>(() => {
    if (!showRisk || typeof normalizedScore !== 'number') {
      return 'Neutral';
    }

    if (normalizedScore <= 30) {
      return 'Happy';
    }

    if (normalizedScore <= 59) {
      return 'Neutral';
    }

    return 'Sad';
  }, [normalizedScore, showRisk]);

  return (
    <Box
      className={`Sod-InsightsList ${className ?? ''} ${
        showRisk ? '' : 'Sod-InsightsList--noRisk'
      }`}>
      <Box className='Sod-InsightsList-header' />
      {showRisk && (
        <Box className='Sod-InsightsList-riskLevel'>
          <Box className='Sod-InsightsList-riskLevel-score'>
            <Box className='Sod-InsightsList-riskLevel-score-meter'>
              <SodRiskScore
                value={normalizedScore}
                size='small'
                kind={riskKind}
                label=''
              />
            </Box>
            <Box className='Sod-InsightsList-riskLevel-score-chip'>
              <Chip
                kind='outlined'
                size='xxSmall'
                label={l(riskLabelKey)}
                color={riskKind}
                leftIcon={
                  <Icon kind={riskIconKind} color={riskKind} size='xSmall' />
                }
              />
            </Box>
          </Box>
        </Box>
      )}
      <TCodeListRow tCodes={tCodeData} onCodeClick={onCodeClick} />
    </Box>
  );
}

InsightsList.defaultProps = {
  className: undefined,
  riskScore: undefined,
};

export default InsightsList;
